import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import locationService from '../../../services/locationService';
import {
    STOCK_ADJUSTMENT_DEFAULT_VISIBLE_COLUMNS,
} from '../../../store/modules/stockadjustment/stockadjustmentSlice';

export const STOCK_ADJUSTMENT_SORT_OPTIONS = [
    { value: 'date-desc', field: 'stock_adjustments.adjustment_date', direction: 'desc', label: 'Date (newest)' },
    { value: 'date-asc', field: 'stock_adjustments.adjustment_date', direction: 'asc', label: 'Date (oldest)' },
    { value: 'qty-desc', field: 'stock_adjustments.quantity', direction: 'desc', label: 'Quantity (high to low)' },
    { value: 'qty-asc', field: 'stock_adjustments.quantity', direction: 'asc', label: 'Quantity (low to high)' },
    { value: 'location-asc', field: 'locations.name', direction: 'asc', label: 'Location (A to Z)' },
    { value: 'number-asc', field: 'stock_adjustments.adjustment_number', direction: 'asc', label: 'Number (A to Z)' },
];

export const STOCK_ADJUSTMENT_COLUMN_OPTIONS = [
    { id: 'adjustment_number', label: 'Adjustment Number', default: true },
    { id: 'adjustment_date', label: 'Adjustment Date', default: true },
    { id: 'location', label: 'Location', default: true },
    { id: 'item', label: 'Item', default: true },
    { id: 'quantity', label: 'Quantity', default: true },
    { id: 'reason', label: 'Reason', default: false },
    { id: 'adjusted_by', label: 'Created By', default: false },
    { id: 'unit_cost', label: 'Unit Cost', default: false },
    { id: 'line_amount', label: 'Line Amount', default: false },
    { id: 'created_at', label: 'Created At', default: false },
    { id: 'notes', label: 'Notes', default: false },
];

export const STOCK_ADJUSTMENT_DATE_PRESETS = [
    { value: 'all', label: 'All dates' },
    { value: 'last7', label: 'Last 7 days' },
    { value: 'last30', label: 'Last 30 days' },
    { value: 'thisMonth', label: 'This month' },
    { value: 'custom', label: 'Custom range' },
];

const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const getStockAdjustmentPresetDates = (preset) => {
    const today = new Date();
    const to = formatDate(today);

    if (preset === 'last7') {
        const fromDate = new Date(today);
        fromDate.setDate(fromDate.getDate() - 6);
        return { from: formatDate(fromDate), to };
    }

    if (preset === 'last30') {
        const fromDate = new Date(today);
        fromDate.setDate(fromDate.getDate() - 29);
        return { from: formatDate(fromDate), to };
    }

    if (preset === 'thisMonth') {
        const fromDate = new Date(today.getFullYear(), today.getMonth(), 1);
        return { from: formatDate(fromDate), to };
    }

    return { from: '', to: '' };
};

export const getStockAdjustmentSortValue = (field, direction) => {
    const match = STOCK_ADJUSTMENT_SORT_OPTIONS.find(
        (option) => option.field === field && option.direction === direction,
    );
    return match?.value || 'date-desc';
};

const StockAdjustmentListToolbar = ({
    state,
    onSearch,
    onLocationChange,
    onDateRangeChange,
    onClearDates,
    onSortChange,
    onToggleColumn,
    onApplyFilters,
}) => {
    const [showColumns, setShowColumns] = useState(false);
    const [datePreset, setDatePreset] = useState('all');
    const [searchValue, setSearchValue] = useState(state?.filters?.search || '');
    const [locations, setLocations] = useState([]);
    const searchTimeoutRef = useRef(null);

    const fromDate = state?.classicFilters?.from_date?.query?.value || '';
    const toDate = state?.classicFilters?.to_date?.query?.value || '';
    const locationValue = state?.classicFilters?.location_id?.query || {};
    const sortValue = getStockAdjustmentSortValue(state?.sort?.field, state?.sort?.direction);
    const visibleColumns = state?.visibleColumns || STOCK_ADJUSTMENT_DEFAULT_VISIBLE_COLUMNS;

    useEffect(() => {
        setSearchValue(state?.filters?.search || '');
    }, [state?.filters?.search]);

    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        let cancelled = false;

        const loadLocations = async () => {
            try {
                const condition = {
                    rules: {
                        condition: 'and',
                        children: [],
                    },
                };
                const response = await locationService.getList({ per_page: 500 }, condition);
                if (cancelled) {
                    return;
                }
                setLocations(
                    (response.data || []).map((item) => ({
                        id: item.id,
                        label: `${item.name?.en || item.name} (${item.location_number})`,
                    })),
                );
            } catch (error) {
                if (!cancelled) {
                    setLocations([]);
                }
            }
        };

        loadLocations();

        return () => {
            cancelled = true;
        };
    }, []);

    const handleSearchChange = useCallback((event) => {
        const value = event.target.value;
        setSearchValue(value);
        onSearch(value);

        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            onApplyFilters({ search: value });
        }, 500);
    }, [onApplyFilters, onSearch]);

    const handleLocationChange = useCallback(async (event) => {
        const value = event.target.value;
        if (!value) {
            await onLocationChange(null);
        } else {
            const location = locations.find((item) => item.id === value);
            await onLocationChange({
                id: value,
                label: location?.label || '',
            });
        }
        await onApplyFilters();
    }, [onApplyFilters, onLocationChange, locations]);

    const handleDatePresetChange = useCallback(async (event) => {
        const preset = event.target.value;
        setDatePreset(preset);

        if (preset === 'custom') {
            return;
        }

        const { from, to } = getStockAdjustmentPresetDates(preset);
        await onDateRangeChange(from, to);
        await onApplyFilters();
    }, [onApplyFilters, onDateRangeChange]);

    const handleFromDateChange = useCallback(async (event) => {
        const value = event.target.value;
        setDatePreset('custom');
        await onDateRangeChange(value, toDate);
        await onApplyFilters();
    }, [onApplyFilters, onDateRangeChange, toDate]);

    const handleToDateChange = useCallback(async (event) => {
        const value = event.target.value;
        setDatePreset('custom');
        await onDateRangeChange(fromDate, value);
        await onApplyFilters();
    }, [fromDate, onApplyFilters, onDateRangeChange]);

    const handleClearDates = useCallback(async () => {
        setDatePreset('all');
        await onClearDates();
        await onApplyFilters();
    }, [onApplyFilters, onClearDates]);

    const handleSortChange = useCallback(async (event) => {
        const option = STOCK_ADJUSTMENT_SORT_OPTIONS.find((item) => item.value === event.target.value);
        if (!option) {
            return;
        }
        await onSortChange(option);
    }, [onSortChange]);

    const handleToggleColumn = useCallback((columnId) => {
        const isVisible = visibleColumns.includes(columnId);
        if (isVisible && visibleColumns.length <= 1) {
            return;
        }

        const nextColumns = isVisible
            ? visibleColumns.filter((id) => id !== columnId)
            : [...visibleColumns, columnId];

        onToggleColumn(nextColumns);
    }, [onToggleColumn, visibleColumns]);

    const showDateInputs = datePreset === 'custom' || fromDate || toDate;
    const defaultColumns = useMemo(
        () => STOCK_ADJUSTMENT_COLUMN_OPTIONS.filter((column) => column.default),
        [],
    );
    const optionalColumns = useMemo(
        () => STOCK_ADJUSTMENT_COLUMN_OPTIONS.filter((column) => !column.default),
        [],
    );

    return (
        <div className="mb-0">
            <div className="d-flex flex-wrap align-items-end gap-2">
                <div className="flex-grow-1" style={{ minWidth: 180 }}>
                    <label className="form-label mb-1 small text-muted">Search</label>
                    <div className="position-relative">
                        <input
                            type="search"
                            className="form-control bg-light border-light rounded"
                            placeholder="Search adjustments..."
                            value={searchValue}
                            onChange={handleSearchChange}
                        />
                        <i className="bx bx-search icon-sm search-icon" />
                    </div>
                </div>

                <div style={{ minWidth: 180 }}>
                    <label className="form-label mb-1 small text-muted">Location</label>
                    <select
                        className="form-select"
                        value={locationValue?.value || ''}
                        onChange={handleLocationChange}
                    >
                        <option value="">All locations</option>
                        {locations.map((location) => (
                            <option key={location.id} value={location.id}>
                                {location.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div style={{ minWidth: 150 }}>
                    <label className="form-label mb-1 small text-muted">Date range</label>
                    <select
                        className="form-select"
                        value={datePreset}
                        onChange={handleDatePresetChange}
                    >
                        {STOCK_ADJUSTMENT_DATE_PRESETS.map((preset) => (
                            <option key={preset.value} value={preset.value}>
                                {preset.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div style={{ minWidth: 170 }}>
                    <label className="form-label mb-1 small text-muted">Sort</label>
                    <select
                        className="form-select"
                        value={sortValue}
                        onChange={handleSortChange}
                    >
                        {STOCK_ADJUSTMENT_SORT_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="form-label mb-1 small text-muted d-block">Columns</label>
                    <button
                        type="button"
                        className={`btn ${showColumns ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => setShowColumns(!showColumns)}
                    >
                        Columns ({visibleColumns.length})
                    </button>
                </div>
            </div>

            {showDateInputs && (
                <div className="d-flex flex-wrap align-items-end gap-2 mt-2">
                    <div>
                        <label className="form-label mb-1 small text-muted">From</label>
                        <input
                            type="date"
                            className="form-control"
                            value={fromDate}
                            onChange={handleFromDateChange}
                        />
                    </div>
                    <div>
                        <label className="form-label mb-1 small text-muted">To</label>
                        <input
                            type="date"
                            className="form-control"
                            value={toDate}
                            onChange={handleToDateChange}
                        />
                    </div>
                    {fromDate && toDate && (
                        <span className="badge bg-light text-dark border mb-1">
                            {fromDate} - {toDate}
                        </span>
                    )}
                    {(fromDate || toDate) && (
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm mb-1"
                            onClick={handleClearDates}
                        >
                            Clear dates
                        </button>
                    )}
                </div>
            )}

            {showColumns && (
                <div className="card mt-2 mb-0">
                    <div className="card-body py-3">
                        <div className="small text-muted text-uppercase fw-semibold mb-2">
                            Default columns
                        </div>
                        <div className="d-flex flex-wrap gap-3 mb-3">
                            {defaultColumns.map((column) => (
                                <label key={column.id} className="form-check-label d-flex align-items-center gap-2 mb-0">
                                    <input
                                        type="checkbox"
                                        className="form-check-input mt-0"
                                        checked={visibleColumns.includes(column.id)}
                                        onChange={() => handleToggleColumn(column.id)}
                                    />
                                    {column.label}
                                </label>
                            ))}
                        </div>
                        <div className="small text-muted text-uppercase fw-semibold mb-2">
                            Additional columns
                        </div>
                        <div className="d-flex flex-wrap gap-3">
                            {optionalColumns.map((column) => (
                                <label key={column.id} className="form-check-label d-flex align-items-center gap-2 mb-0">
                                    <input
                                        type="checkbox"
                                        className="form-check-input mt-0"
                                        checked={visibleColumns.includes(column.id)}
                                        onChange={() => handleToggleColumn(column.id)}
                                    />
                                    {column.label}
                                </label>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StockAdjustmentListToolbar;
